import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, Subject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, share, switchMap, catchError, startWith } from 'rxjs/operators';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { LoggingService } from '@Services/logging.service';
import { HttpHeaderNames } from '@Core/enums/http-header-names';
import { environment } from 'src/environments/environment';
import _ from 'lodash';

import { AuthTenant } from '../enums/auth-tenant.enum';
import { UnifiedAuthContext } from '../interfaces/auth-config.interface';
import { AuthTenantContextService } from './auth-tenant-context.service';
import { Auth0ProviderFactory, IAuth0Provider } from './auth0-provider-factory.service';
import { AuthContext, ScpAuthContext } from '../interfaces/auth-context.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public authLoaded: boolean = false;
    public errorMessage: string | null = null;
    public loading: boolean = false;

    private _authInitializing: boolean = false;
    private _spaName: string;
    private _isRefreshing: boolean;
    private baseUrl: string;

    private authContext: UnifiedAuthContext;
    private _authContextSubject = new BehaviorSubject<UnifiedAuthContext | null>(null);
    private _logOutRequest: Observable<any>;
    private scpAuthContextLoaded: string; // "Tenant:Area"

    // SCP Authentication properties
    // These are preset to a non-matchable placeholder so that the MSAL interceptor's
    // protectedResourceMap (in scp-auth.module.ts) never accidentally matches real
    // HTTP requests before scpAuthContext() populates the real URLs.
    private static readonly SCP_UNSET = 'https://scp-not-configured.invalid';
    private _scpAuthorities: {[domain: string]: string} = {};
    private _scpClientId: string;
    private _policyRestApi: string = AuthService.SCP_UNSET;
    private _producerRestApi: string = AuthService.SCP_UNSET;
    private _policyCommandApi: string = AuthService.SCP_UNSET;
    private _policyResultsApi: string = AuthService.SCP_UNSET;
    private _oauthScope: string = AuthService.SCP_UNSET;
    private _clickOnceUri: string = AuthService.SCP_UNSET;

    // State management for UI components
    public isAdmin: boolean = false;
    public tenants: { key: string; name: string }[] = [];
    public selectedTenant: string;
    public areas: string[] = [];

    // Auth0 provider instance
    private auth0Provider: IAuth0Provider | null = null;

    constructor(
        private http: HttpClient,
        private logger: LoggingService,
        private router: Router,
        private authTenantContext: AuthTenantContextService,
        private auth0ProviderFactory: Auth0ProviderFactory
    ) {
        this.baseUrl = environment.applicationEndpoint + (environment.requireAPIM ? '/Auth' : '/Admin');
    }

    // BehaviorSubject for authContext.
    // Lazily triggers authentication loading if it hasn't started yet,
    // and filters out the initial null value until auth has been checked.
    public get authContext$(): Observable<UnifiedAuthContext | null> {
        if (!this.authLoaded && !this._authInitializing) {
            this.initializeAuth();
        }
        return this._authContextSubject.pipe(
            filter(ctx => ctx !== null || this.authLoaded)
        );
    }

    /**
     * Initialize authentication by checking for existing session
     * Call this method when you want to check if user is already authenticated
     */
    public initializeAuth(): void {
        if (!this.authLoaded && !this._authInitializing) {
            this._authInitializing = true;
            this.logger.logVerbose("Initializing user authentication check.");
            this.getCurrentUser();
        }
    }

    // Public getters for URLs
    public get LogoutURL() {
        return `${this.baseUrl}/Users/Logout`;
    }

    public get CurrentUserURL() {
        return `${this.baseUrl}/CurrentUser`;
    }

    public get InsurityBITokenURL() {
        return `${this.baseUrl}/InsurityBIToken`;
    }

    public get AdminTenantTokenURL() {
        return `${this.baseUrl}/Users/TenantAreaToken`;
    }

    public set spaName(name: string) {
        this._spaName = name;
    }
    public get spaName() {
        return this._spaName;
    }

    public get BearerToken() {
        return this.authContext?.bearerToken;
    }

    public get isAuthenticated$(): Observable<boolean> {
        return this.authContext$.pipe(switchMap((authContext) => {
            return of(this.isAuthenticated(authContext));
        }));
    }

    public isAuthenticated(authContext: AuthContext): boolean {
        if (!authContext?.expiration) {
            this.logger.logInfo("User's token does not have an expiration date.");
            return false;
        }
        if (new Date() < authContext.expiration) {
            return true;
        } else {
            this.logger.logInfo("User's token has expired.");
            return false;
        }
    }

    // Auth0 Observable Wrappers - delegate to the current provider
    public get idTokenClaims$(): Observable<any> {
        return new Observable(subscriber => {
            this.getAuth0Provider().then(provider => {
                if (provider.idTokenClaims$) {
                    provider.idTokenClaims$.subscribe(subscriber);
                } else {
                    subscriber.next(null);
                    subscriber.complete();
                }
            }).catch(error => subscriber.error(error));
        });
    }

    public get error$(): Observable<Error> {
        return new Observable(subscriber => {
            this.getAuth0Provider().then(provider => {
                if (provider.error$) {
                    provider.error$.subscribe(subscriber);
                } else {
                    subscriber.next(null);
                    subscriber.complete();
                }
            }).catch(error => subscriber.error(error));
        });
    }

    // #region SCP Authentication
    public get policyRestApi() {
        return this._policyRestApi;
    }

    public get producerRestApi() {
        return this._producerRestApi;
    }

    public get policyCommandApi() {
        return this._policyCommandApi;
    }

    public get policyResultsApi() {
        return this._policyResultsApi;
    }

    public get oauthScope() {
        return this._oauthScope;
    }

    public get clickOnceUri() {
        return this._clickOnceUri;
    }

    public get scpClientId(): string {
        return this._scpClientId;
    }

    public get scpAuthority() {
        return AuthService.deriveAuthorityFromDomain(this._scpAuthorities, this.authContext?.email);
    }

    public static deriveAuthorityFromDomain(domainAuthorities: {[domain: string]: string}, email: string) {
        if (_.isNil(domainAuthorities))
            return "";

        if (Object.entries(domainAuthorities).length == 0 || !email)
            return "";

        var domain = _.split(email, '@')[1];
        var authority = domainAuthorities[domain];
        if (authority) {
            return authority;
        } else {
            // Return the first authority if no match is found
            var firstEntry = _.first(Object.entries(domainAuthorities));
            return firstEntry[1];
        }
    }

    public getScpAuthContextUrl() {
        return environment.applicationEndpoint + '/Policy/Area';
    }

    public getScpAuthContext() {
        return this.http.get<any>(this.getScpAuthContextUrl()).pipe(share());
    }

    public scpAuthContext(): Observable<ScpAuthContext> {
        // If we have already loaded this tenant/area combo, just return it.
        if (this.scpAuthContextLoaded == this.authContext?.tenantKey + ":" + this.authContext?.areaKey
            && Object.entries(this._scpAuthorities).length) {
            return of({
                clientId: this._scpClientId,
                authority: this.scpAuthority,
                policyAPI: this._policyRestApi,
                producerAPI: this._producerRestApi,
                policyCommandAPI: this._policyCommandApi,
                policyResultAPI: this._policyResultsApi,
                oauthScopes: [this._oauthScope],
                clickOnceUri: this._clickOnceUri,
            });
        } else {
            let request = this.getScpAuthContext() // tell the browser to accept the set-cookie header
                .pipe(share());

            request.subscribe(response => {
                let content = response.Content;
                this._scpAuthorities = content['#BureauDomainAuthorities'];
                this._policyRestApi = content['#BureauPolicyAPI_URI'];
                this._producerRestApi = content['#BureauProducerAPI_URI'];
                this._policyCommandApi = content['#BureauPolicyUI_URI'];
                this._policyResultsApi = content['#BureauPolicyResults_URI'];
                this._oauthScope = content['#BureauOAuthScopes'];
                this._clickOnceUri = content['#BureauClickOnce_URI'];
                this._scpClientId = content["#BureauClientID"];

                this.scpAuthContextLoaded = this.authContext?.tenantKey + ":" + this.authContext?.areaKey;
            });

            return request;
        }
    }
    // #endregion SCP Authentication

    // #region Auth Context Helper Methods
    public getSubscriptionKey(): string {
        return this.authContext?.subscription;
    }

    public getIdentityKey() {
        return this.authContext?.identityKey;
    }

    public getTenantKey() {
        return this.authContext?.tenantKey;
    }

    /**
     * Return the tenant's display name when available, otherwise fall back to the tenant key.
     */
    public getTenantDisplayName() {
        return this.authContext?.tenantDisplayName || this.authContext?.tenantKey;
    }

    public getAreaKey() {
        return this.authContext?.areaKey;
    }

    public isProgramManager(): boolean {
        return !!this.authContext?.managedPrograms?.length;
    }

    public getManagedPrograms(): string[] {
        return this.authContext?.managedPrograms;
    }

    public getExpiration(): Date {
        return this.authContext?.expiration;
    }

    public getAvailableTenants(): string[] {
        return this.authContext?.tenants;
    }

    public getAvailableAreasForCurrentTenant(): string[] {
        return this.authContext?.areas;
    }

    public get currentAuthTenant(): AuthTenant | null {
        return this.authContext?.authTenant || null;
    }
    // #endregion

    // #region Authentication Flow Methods

    /**
     * Login with the currently selected Auth0 tenant
     */
    public async login(): Promise<void> {
        this.loading = true;
        try {
            const provider = await this.getAuth0Provider();
            await provider.loginWithRedirect({});
        } catch (err: any) {
            this.errorMessage = err?.message || 'Login failed';
            this.loading = false;
        }
    }

    /**
     * Login with a specific Auth0 tenant
     */
    public async loginWithTenant(tenant: AuthTenant): Promise<void> {
        // Set the tenant context before logging in
        this.authTenantContext.setSelectedTenant(tenant);
        // Clear the current provider so it gets recreated with the new tenant
        this.auth0Provider = null;
        await this.login();
    }

    /**
     * Handle redirect callback - works for both Auth0 tenants
     */
    public async handleRedirectCallback(): Promise<void> {
        this.loading = true;
        this.errorMessage = null;

        try {
            const provider = await this.getAuth0Provider();
            await provider.handleRedirectCallback();
            const claims = await provider.getIdTokenClaims();

            if (claims && claims.__raw) {
                this.generateClaims(claims.__raw).subscribe({
                    next: () => {
                        this.loading = false;
                    },
                    error: (error) => {
                        this.logger.logException(error, SeverityLevel.Error);
                        this.errorMessage = 'Token validation failed';
                        this.loading = false;
                    }
                });
            } else {
                this.logger.logInfo('No valid token claims received after redirect callback.');
                this.errorMessage = 'No valid token received';
                this.loading = false;
            }
        } catch (err: any) {
            this.logger.logException(err, SeverityLevel.Error);
            this.errorMessage = err?.message || 'Callback failed';
            this.loading = false;
            throw err;
        }
    }

    public generateClaims(bearerToken: string): Observable<Response> {
        this.loading = true;
        this.errorMessage = null;

        const headers: HttpHeaders = new HttpHeaders()
            .set(HttpHeaderNames.ContentType, 'application/json')
            .set(HttpHeaderNames.Authorization, "bearer " + bearerToken);

        let request = this.http.get<any>(`${this.baseUrl}/Users/Login?browserMode=true`,
            { headers: headers, withCredentials: true })
            .pipe(share());

        request.subscribe({
            next: response => {
                let content = response.Content;
                this.populateTenants(content);
                this.setValues(content);
                this.checkTenants(response);
            },
            error: (error: HttpErrorResponse) => {
                this.logger.logException(error, SeverityLevel.Error);
                this.loading = false;
                if (error.status === 429) {
                    this.errorMessage = 'The system is currently busy';
                } else if (error.status === 403) {
                    this.errorMessage = 'You no longer have access. Contact your administrator.';
                } else {
                    this.errorMessage = `Login API error: ${error.status} - ${error.message}`;
                }
            }
        });

        return request;
    }

    private checkTenants(response: any): void {
        // Check if we're currently handling a callback on altlogin page
        const isOnAltLoginPage = window.location.pathname === '/altlogin'
            || this.router.url === '/altlogin';
        const hasCallbackParams = window.location.search.includes('code=')
            && window.location.search.includes('state=');

        if (isOnAltLoginPage && hasCallbackParams) {
            // Set up the UI state but don't navigate away from altlogin during callback
            if (response.Content['admin'] || response.Content['tenantKey']) {
                this.loading = false;
                return;
            }
            this.mapAndProcessTenants(response, false);
            return;
        }

        // Normal navigation flow when not handling callback on altlogin
        if (response.Content['admin']) {
            this.loading = false;
            this.router.navigate(['Admin']);
            return;
        } else if (response.Content['tenantKey']) {
            this.loading = false;
            this.router.navigate(['SelectArea']);
            return;
        }
        this.mapAndProcessTenants(response, true);
    }

    /**
     * Build the tenants array from raw response content.
     */
    private populateTenants(content: any): void {
        if (content?.tenants) {
            const tenantKeys: string[] = content.tenants || [];
            const tenantNames: string[] = content.tenantNames || [];
            this.tenants = _.orderBy(
                _.map(tenantKeys, (k, i) => ({ key: k, name: (tenantNames[i] || k) })),
                t => t.name
            );
        }
    }

    /**
     * Shared helper: maps tenants from the login response and either auto-selects
     * a single tenant or prepares multi-tenant selection UI.
     * @param logIfEmpty - log when no tenants are found (skipped during callback)
     */
    private mapAndProcessTenants(response: any, logIfEmpty: boolean): void {
        this.populateTenants(response.Content);

        if (this.tenants.length) {
            if (this.tenants.length === 1) {
                this.tenantSelect(this.tenants[0]);
                return;
            }

            this.selectedTenant = null;
            this.loading = false;
        } else {
            if (logIfEmpty) {
                this.logger.logInfo('No tenant information found in login response.');
            }
            this.loading = false;
        }
    }

    public logout(redirect: boolean = true): Observable<Response> {
        if (this._logOutRequest) {
            return this._logOutRequest;
        }

        let request = this._logOutRequest = this.http.post<any>(this.LogoutURL, null).pipe(share());

        request.subscribe({
            next: response => {
                this.authContext = null;
            },
            error: error => {
                this._logOutRequest = null;
                if (error.status != 401) {
                    this.logger.logException(error, SeverityLevel.Error);
                }
            },
            complete: () => {
                this._logOutRequest = null;
                if (redirect) {
                    this.auth0Logout();
                }
            }
        });

        return request;
    }

    public async auth0Logout(): Promise<void> {
        const provider = await this.getAuth0Provider();
        await provider.logout({ logoutParams: { returnTo: environment.baseUrl } });
    }

    // #endregion

    // #region Server Communication Methods

    public getCurrentUser(): Observable<Response> {
        let request = this.http.get<any>(this.CurrentUserURL, {
            // For any other request, the interceptor would add this option, 
            // but we have to explicitly specify it here because
            // this is used by the interceptor to add the option to all _other_ requests.
            withCredentials: true,
        }).pipe(share());

        request.subscribe({
            next: response => {
                let content = response.Content;
                this.populateTenants(content);
                this.setValues(content);
                this.authLoaded = true;
                this.logger.logVerbose("User authentication loaded.");
            },
            error: error => {
                this.authLoaded = true;
                this.logger.logInfo("Failed to get current user, user may not be authenticated");
                // Don't automatically logout on 401 errors to prevent infinite loops
                // Let the auth guards and components handle authentication state
                if (error.status !== 401) {
                    this.logger.logException(error, SeverityLevel.Error);
                }
                // Set authContext to null to indicate no authentication
                this.authContext = null;
                this._authContextSubject.next(null);
            }
        });

        return request;
    }

    public tenantSelect(tenant: { key: string, name: string }): Observable<any> {
        const tenantKey: string = tenant?.key;

        if (!tenantKey || typeof tenantKey !== 'string') {
            this.logger.logInfo('Invalid tenant key provided to tenantSelect.');
            this.errorMessage = 'Invalid tenant selected';
            return of(null);
        }

        this.loading = true;
        const headers: HttpHeaders = new HttpHeaders()
            .set(HttpHeaderNames.ContentType, 'application/json');
        const requestBody = { TenantKey: tenantKey };

        let request = this.http.post<any>(
            `${this.baseUrl}/Users/SelectTenant?browserMode=true`,
            requestBody,
            { headers: headers, withCredentials: true }
        ).pipe(share());

        request.subscribe({
            next: response => {
                let content = response.Content;
                this.setValues(content);
                this.router.navigate(['SelectArea']).finally(() => {
                    this.loading = false;
                });
            },
            error: (error) => {
                this.logger.logException(error, SeverityLevel.Error);
                this.loading = false;
            }
        });

        return request;
    }

    public selectArea(areaKey: string) {
        if (!areaKey || typeof areaKey !== 'string') {
            this.logger.logInfo('Invalid area key provided to selectArea.');
            this.errorMessage = 'Invalid area selected';
            return of(null);
        }

        this.loading = true;

        let request = this.http.post<any>(
            `${this.baseUrl}/Users/SelectArea?browserMode=true`, { areaKey }
        ).pipe(share());

        request.subscribe({
            next: response => {
                let content = response.Content;
                this.setValues(content);
                this.loading = false;
            },
            error: (error) => {
                this.logger.logException(error, SeverityLevel.Error);
                this.loading = false;
            }
        });

        return request;
    }

    public insurityBIToken(): Observable<Response> {
        let request = this.http.get<any>(this.InsurityBITokenURL, {
            withCredentials: true,
        }).pipe(share());

        request.subscribe({
            next: response => { },
            error: error => { this.auth0Logout(); }
        });

        return request;
    }

    public getAdminTenantAreaToken(optionalParameters: {
        tenantKey: string,
        areaKey?: string,
    }): Observable<Response> {

        let params = new HttpParams();
        params = params.set('tenantKey', optionalParameters.tenantKey);

        if (optionalParameters?.areaKey != null)
            params = params.set('areaKey', optionalParameters.areaKey);

        let request = this.http.get<any>(this.AdminTenantTokenURL, {
            params,
            withCredentials: true,
        }).pipe(share());

        request.subscribe({
            next: () => { },
            error: () => { }
        });

        return request;
    }

    public refresh() {
        if (this._isRefreshing) {
            return;
        }

        this._isRefreshing = true;
        this.logger.logVerbose('Refreshing authentication token.');

        let request = this.http.get<any>(`${this.baseUrl}/Users/Refresh?browserMode=true`).subscribe({
            next: response => {
                let content = response.Content;
                this.setValues(response.Content);
                this._isRefreshing = false;
            },
            error: err => {
                this._isRefreshing = false;
                if (err instanceof HttpErrorResponse) {
                    const resp: HttpErrorResponse = err as HttpErrorResponse;
                    if (resp.status == 403) {
                        this.logout();
                    }
                }
            }
        });
    }

    public recoverPassword(email: string) {
        const headers: HttpHeaders = new HttpHeaders().set(HttpHeaderNames.ContentType, 'application/json');
        const request = this.http.post<any>(`${this.baseUrl}/Users/SendResetPassword`, JSON.stringify(email), {
            headers: headers
        }).pipe(share());

        request.subscribe(response => {});
        return request;
    }

    public resetUserAuthentication(userId: string) {
        const headers: HttpHeaders = new HttpHeaders().set(HttpHeaderNames.ContentType, 'application/json');
        const request = this.http.put(`${this.baseUrl}/Users/` + userId + '/ResetAuth', null, {
            headers: headers
        }).pipe(share());

        request.subscribe(response => { });
        return request;
    }

    public switchUserAuthProvider(userId: string) {
        const headers: HttpHeaders = new HttpHeaders().set(HttpHeaderNames.ContentType, 'application/json');
        const request = this.http.put(`${this.baseUrl}/Users/` + userId + '/ResetAuth?switchAuthProvider=true', null, {
            headers: headers
        }).pipe(share());

        request.subscribe(response => { });
        return request;
    }

    public resetUsersAuthenticationByDomain(domains: string[]) {
        const headers: HttpHeaders = new HttpHeaders().set(HttpHeaderNames.ContentType, 'application/json');
        const request = this.http.put(`${this.baseUrl}/Users/ResetAuthByDomains`, JSON.stringify(domains), {
            headers: headers
        }).pipe(share());

        request.subscribe(response => { });
        return request;
    }

    public switchUsersAuthProviderByDomain(domains: string[]) {
        const headers: HttpHeaders = new HttpHeaders().set(HttpHeaderNames.ContentType, 'application/json');
        const request = this.http.put(`${this.baseUrl}/Users/ResetAuthByDomains?switchAuthProvider=true`, JSON.stringify(domains), {
            headers: headers
        }).pipe(share());

        request.subscribe(response => { });
        return request;
    }

    // #endregion

    // #region Private Helper Methods

    private async getAuth0Provider(): Promise<IAuth0Provider> {
        if (!this.auth0Provider) {
            this.auth0Provider = await this.auth0ProviderFactory.getProvider();
        }
        return this.auth0Provider;
    }

    private setValues(content: any) {
        if (content) {
            this.isAdmin = !!content.admin;
            this.authContext = {
                identityKey: content.identityKey,
                tenantKey: content.tenantKey,
                tenantDisplayName: content.tenantName || content.tenantKey,
                areaKey: content.areaKey,
                subscription: content.subscription,
                expiration: content.expires ? new Date(content.expires) : null,
                managedPrograms: AuthService.calculateManagedPrograms(content.managedPrograms),
                tenants: content.tenants,
                areas: content.areas,
                bearerToken: "cookie",
                email: content.email,
                authTenant: this.authTenantContext.getSelectedTenant()
            };
        }

        this._authContextSubject.next(this.authContext);
    }

    public static calculateManagedPrograms(claim: string): string[] {
        if (claim) {
            let managedPrograms = [];
            let programs = claim.split('|');
            for (let program of programs) {
                if (program.includes('*'))
                    managedPrograms.push(program.replace('*', ''));
            }
            return managedPrograms;
        }
        return [];
    }

    // #endregion
}

